import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  /** Optional leading icon (e.g. a lucide icon element). */
  icon?: ReactNode;
  /** Stretch to fill the container width. */
  fullWidth?: boolean;
};

// Press feedback (scale 0.97 on :active) is applied globally in index.css to every
// enabled <button>, so it isn't repeated here. We only own variant/size/focus here.
const base =
  'inline-flex items-center justify-center gap-2 rounded-full font-medium ' +
  'transition-colors duration-pop ease-out-strong ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60 ' +
  'disabled:opacity-50 disabled:pointer-events-none select-none';

const variants: Record<Variant, string> = {
  primary: 'bg-emerald-500 text-emerald-950 hover:bg-emerald-400',
  secondary: 'bg-white/[0.08] text-fg hover:bg-white/[0.14]',
  ghost: 'text-fg-2 hover:text-white hover:bg-white/[0.06]',
  danger: 'border border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20',
};

const sizes: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', icon, fullWidth, className, children, type, ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={cn(base, variants[variant], sizes[size], fullWidth && 'w-full', className)}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
});

export default Button;
